import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button/Button";
import { Input } from "@/components/ui/input/Input";
import { useToast } from "@/lib/hooks/useToast";
import { Trash2, Plus, Pencil, Home } from "lucide-react";
import ManagementService from "@/lib/services/management/ManagementService";
import { IPIC } from "@/models/management/PIC";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { AddPICDialog } from "./AddPICDialog";
import { EditPICDialog } from "./EditPICDialog";
import { ViewRoomsDialog } from "./ViewRoomsDialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination/Pagination";

export default function PICManagementTable({ setLoading }: { setLoading: (l: boolean) => void }) {
  const [pics, setPics] = useState<IPIC[]>([]);
  const [search, setSearch] = useState("");
  const { toast } = useToast();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isRoomsOpen, setIsRoomsOpen] = useState(false);
  
  const [selectedPIC, setSelectedPIC] = useState<{ id: number; initial: string } | null>(null);
  const [editingPIC, setEditingPIC] = useState<IPIC | null>(null);
  const [viewingInitial, setViewingInitial] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchPICs = async () => {
    setLoading(true);
    try {
      const data = await ManagementService.getPICs(search);
      setPics(data);
      setCurrentPage(1);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPICs();
  }, []);

  const totalPages = Math.ceil(pics.length / itemsPerPage);
  const currentData = pics.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleAddPIC = async (formData: any) => {
    setIsAddDialogOpen(false);
    setLoading(true);
    try {
      await ManagementService.createPIC(formData);
      toast({ title: "Success", description: "New PIC assigned successfully." });
      fetchPICs();
    } catch (error) {
      toast({ title: "Error", description: "Failed to assign PIC.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleEditPIC = async (formData: any) => {
    if (!editingPIC) return;
    setIsEditDialogOpen(false);
    setLoading(true);
    try {
      await ManagementService.updatePIC(editingPIC.id, formData);
      toast({ title: "Success", description: "PIC updated successfully." });
      fetchPICs();
    } catch (error) {
      toast({ title: "Error", description: "Update failed.", variant: "destructive" });
    } finally {
      setLoading(false);
      setEditingPIC(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedPIC) return;
    setIsDeleteDialogOpen(false);
    setLoading(true);
    try {
      await ManagementService.deletePIC(selectedPIC.id);
      toast({ title: "Success", description: "PIC removed successfully." });
      fetchPICs();
    } catch (error) {
      toast({ title: "Error", description: "Delete failed.", variant: "destructive" });
    } finally {
      setLoading(false);
      setSelectedPIC(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* header */}
      <div className="flex justify-between items-center bg-card p-4 rounded-lg shadow-sm border border-border">
        <div className="flex gap-2 items-center">
          <Input
            className="w-64 bg-background border-input text-foreground font-lregular h-9"
            placeholder="Search Initial or Name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchPICs()}
          />
          <Button variant="blue" size="sm" onClick={fetchPICs} className="px-4 h-9 shadow-sm">
            Filter
          </Button>
        </div>

        <Button
          variant="blue"
          onClick={() => setIsAddDialogOpen(true)}
          className="w-auto flex gap-2 items-center h-9 px-4 shadow-md transition-all hover:opacity-90 active:scale-95 whitespace-nowrap"
        >
          <Plus size={16} strokeWidth={3} />
          <span className="font-lsemibold text-sm">Assign New PIC</span>
        </Button>
      </div>

      {/* table */}
      <div className="bg-card rounded-lg shadow-md overflow-hidden border border-border">
        <table className="w-full text-left border-collapse font-lregular table-fixed">
          <thead>
            <tr className="border-b border-border bg-secondary/30 dark:bg-secondary/10">
              <th className="px-6 py-4 text-sm font-lsemibold text-foreground w-[10%]">ID</th>
              <th className="px-6 py-4 text-sm font-lsemibold text-foreground w-[25%]">Initial / Name</th>
              <th className="px-6 py-4 text-sm font-lsemibold text-foreground w-[30%]">Email</th>
              <th className="px-6 py-4 text-sm font-lsemibold text-foreground w-[15%]">Role</th>
              <th className="px-6 py-4 text-sm font-lsemibold text-foreground text-center w-[20%]">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {currentData.length > 0 ? (
              currentData.map((pic) => (
                <tr key={pic.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-6 py-4 text-sm text-muted-foreground font-lregular">#{pic.id}</td>
                  <td className="px-6 py-4 text-sm">
                    <div className="font-lsemibold text-foreground truncate">{pic.initial}</div>
                    <div className="text-xs text-muted-foreground font-llight truncate">
                      {pic.name || "No Name"}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground/80 font-lregular truncate">
                    {pic.email || "-"}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span className="px-2 py-1 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-[10px] font-lsemibold whitespace-nowrap">
                      {pic.role?.name || "No Role"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex justify-center gap-1">
                      {/* View Rooms */}
                      <Button
                        variant="ghost"
                        className="text-emerald-500 hover:bg-emerald-500/10 p-2 h-auto"
                        onClick={() => {
                          setViewingInitial(pic.initial);
                          setIsRoomsOpen(true);
                        }}
                        title="View Rooms"
                      >
                        <Home size={16} />
                      </Button>
                      
                      {/* Edit */}
                      <Button
                        variant="ghost"
                        className="text-blue-500 hover:bg-blue-500/10 p-2 h-auto"
                        onClick={() => {
                          setEditingPIC(pic);
                          setIsEditDialogOpen(true);
                        }}
                        title="Edit PIC"
                      >
                        <Pencil size={16} />
                      </Button>

                      {/* Delete */}
                      <Button
                        variant="ghost"
                        className="text-destructive hover:bg-destructive/10 p-2 h-auto"
                        onClick={() => {
                          setSelectedPIC({ id: pic.id, initial: pic.initial });
                          setIsDeleteDialogOpen(true);
                        }}
                        title="Remove PIC"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-6 py-10 text-center text-muted-foreground italic font-llight">
                  No PICs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* pagination */}
        <div className="p-4 flex justify-center border-t border-border bg-secondary/5">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage > 1) setCurrentPage(currentPage - 1);
                  }}
                />
              </PaginationItem>
              {[...Array(totalPages)].map((_, i) => (
                <PaginationItem key={i}>
                  <PaginationLink
                    href="#"
                    isActive={currentPage === i + 1}
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage(i + 1);
                    }}
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>

      {/* dialogs */}
      <AddPICDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onConfirm={handleAddPIC}
      />

      <EditPICDialog
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onConfirm={handleEditPIC}
        pic={editingPIC}
      />

      <ViewRoomsDialog
        isOpen={isRoomsOpen}
        onClose={() => setIsRoomsOpen(false)}
        picInitial={viewingInitial}
      />

      <ConfirmDeleteDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Remove PIC"
        description={`Are you sure you want to remove ${selectedPIC?.initial}?`}
      />
    </div>
  );
}